import * as React from "react";
import { getLayout, LayoutCapabilities } from "../api/registry/layout";
import {
    IExternalBaseLayer,
    IBranchedMapSubState,
    ClientKind,
    InitError,
    IMapViewer,
    Dictionary
} from "../api/common";
import { IInitAppLayout, initLayout } from "../actions/init";
import { Error, normalizeStack } from "../components/error";
import { tr, DEFAULT_LOCALE } from "../api/i18n";
import { getAssetRoot } from "../utils/asset";
import { setFusionRoot } from "../api/runtime";
import { AppContext, AppContextProvider, IApplicationContext, LegendNodeExtraHTMLProps } from "../components/context";
import { IElementState } from '../actions/defs';
import { NonIdealState, Spinner, Intent, Callout } from '@blueprintjs/core';
import { useInitError, useInitErrorStack, useInitErrorOptions, useViewerLocale, useActiveMapBranch, useActiveMapName, useViewerFeatureTooltipsEnabled } from './hooks';
import { areStatesEqual, getStateFromUrl, IAppUrlState, updateUrl } from './url-state';
import { debug } from '../utils/logger';
import { setElementStates } from '../actions/template';
import { IViewerInitCommand } from '../actions/init-command';
import { ApplicationDefinition } from '../api/contracts/fusion';
import { useReduxDispatch } from "../components/map-providers/context";
import { sanitize } from "dompurify";
import { MapGroup, MapLayer } from "../api/contracts/runtime-map";

export interface SelectionOptions {
    allowHtmlValues?: boolean;
    cleanHtml?: (value: string) => string;
}

/**
 * @since 0.14.9
 */
export interface LegendOptions {
    /**
     * Provide extra HTML elements to insert before a layer name in a layer legend node
     * 
     * Please note: If the HTML returned by this function would contain potentially untrusted input, you 
     * are responsible for sanitizing the HTML content. The given options contains a sanitize function for
     * you to sanitize untrusted parts of the HTML content being assembled.
     * 
     * @param options
     */
    provideExtraLayerIconsHtml?: (options: LegendNodeExtraHTMLProps<MapLayer>) => string[];
    /**
     * Provide extra HTML elements to insert before a group name in a group legend node
     * 
     * Please note: If the HTML returned by this function would contain potentially untrusted input, you 
     * are responsible for sanitizing the HTML content. The given options contains a sanitize function for
     * you to sanitize untrusted parts of the HTML content being assembled.
     * 
     * @param options
     */
    provideExtraGroupIconsHtml?: (options: LegendNodeExtraHTMLProps<MapGroup>) => string[];
}

/**
 * @since 0.14.9 Extracted out to separate interface
 */
export interface IInitialElementVisibility {
    taskpane: boolean;
    legend: boolean;
    selection: boolean;
}

/**
 * MapGuide-specific application options
 *
 * @export
 * @interface IMapGuideAppProps
 * @since 0.14
 */
export interface IMapGuideAppProps {
    /**
     * A session id to init this viewer with
     */
    session?: string;
    /**
     * The mapagent URI
     *
     * @type {string}
     * @memberof IMapGuideAppProps
     */
    agentUri: string;
    /**
     * The agent kind
     *
     * @type {ClientKind}
     * @memberof IMapGuideAppProps
     */
    agentKind?: ClientKind;
    /**
     * The base URL for fusion. Used to determine the paths to the PHP backends for servicing tools like buffer/theme/featureinfo/redline/theme/etc
     */
    fusionRoot: string;
    /**
     * Settings that control the selection panel (if provided by the template)
     */
    selectionSettings?: SelectionOptions;
    /**
     * Settings that control the legend (if provided by the template)
     */
    legendSettings?: LegendOptions;
    /**
     * Defines initial element visibility
     */
    initialElementVisibility: IInitialElementVisibility;
}

/**
 * Describes an ad-hoc layout template
 * 
 * @since 0.14
 */
export type AdHocLayoutTemplate = {
    factory: (() => React.ReactNode);
    capabilities: LayoutCapabilities;
}

/**
 * App component properties
 *
 * @export
 * @interface IAppProps
 */
export interface IAppProps {
    /**
     * The command that will carry out viewer initialization
     *
     * @type {IViewerInitCommand}
     * @memberof IAppProps
     * @since 0.14
     */
    initCommand: IViewerInitCommand;
    /**
     * The layout to use
     * @since 0.14 type changed to include AdHocLayoutTemplate
     */
    layout: string | AdHocLayoutTemplate;
    /**
     * A resource id to a Web Layout or Application Definition or a function that will fetch the required Application Definition. 
     *
     * @memberof IAppProps
     */
    resourceId: string | (() => Promise<ApplicationDefinition>);
    /**
     * The list of external base layers
     *
     * @type {IExternalBaseLayer[]}
     * @memberof IAppProps
     * 
     * @remarks This option is for when you are passing in a Web Layout to the viewer, but you still want to provide external 
     * base layers. If passing in an Application Definition, this option is redundant as the Application Definition itself can
     * define the set of external base layers
     */
    externalBaseLayers?: IExternalBaseLayer[];
    /**
     * If specified, tells the application to not sync the following URL params to the URL, and to skip
     * reading these params when initializing/refreshing.
     *
     * @type {string[]}
     * @memberof IAppProps
     * @since 0.14
     */
    urlPropsIgnore?: string[];
    /**
     * A callback function that will be called when the viewer has finished initializing
     */
    onInit?: (viewer: IMapViewer) => void;
    /**
     * The locale to use
     */
    locale?: string;
    /**
     * MapGuide-specific options
     *
     * @type {IMapGuideAppProps}
     * @memberof IAppProps
     * @since 0.14
     */
    mapguide?: IMapGuideAppProps;
    /**
     * @since 0.14.8
     */
    appSettings?: Dictionary<string> | undefined;
}

/**
 * App component state
 *
 * @export
 * @interface IAppState
 */
export interface IAppState {
    error: InitError | undefined;
    includeStack: boolean;
    initOptions: any;
    //config: IConfigurationReducerState;
    configuredLocale: string;
    map: IBranchedMapSubState | undefined;
    activeMapName: string | undefined;
    featureTooltipsEnabled: boolean;
}

/**
 * App component action dispatchers
 *
 * @export
 * @interface IAppDispatch
 */
export interface IAppDispatch {
    initLayout: (cmd: IViewerInitCommand, args: IInitAppLayout) => void;
    setElementVisibility: (states: IElementState) => void;
}

type AppInnerProps = IAppProps & IAppState & IAppDispatch;

class AppInner extends React.Component<AppInnerProps, any> {
    constructor(props: AppInnerProps) {
        super(props);
        this.state = {
            isLoading: true
        };
    }
    componentDidMount() {
        const {
            onInit,
            setElementVisibility,
            mapguide,
            initLayout,
            locale,
            resourceId,
            externalBaseLayers,
            initCommand,
            appSettings
        } = this.props;
        const {
            locale: urlLocale,
            resource: urlResource,
            session: urlSession,
            x: urlX,
            y: urlY,
            ft: urlFeatureTooltip,
            scale: urlScale,
            map: urlMap,
            sl: urlShowLayers,
            hl: urlHideLayers,
            sg: urlShowGroups,
            hg: urlHideGroups
        } = getStateFromUrl(this.props.urlPropsIgnore);
        if (setElementVisibility && mapguide?.initialElementVisibility) {
            const { taskpane, legend, selection } = mapguide.initialElementVisibility;
            const states: IElementState = {
                taskPaneVisible: typeof (taskpane) != 'undefined' ? taskpane : true,
                legendVisible: typeof (legend) != 'undefined' ? legend : true,
                selectionPanelVisible: typeof (selection) != 'undefined' ? selection : true
            };
            setElementVisibility(states);
        }
        debug(`Asset root is: ${getAssetRoot()}`);
        if (mapguide?.fusionRoot) {
            setFusionRoot(mapguide.fusionRoot);
        }
        if (initLayout) {
            let ftArgs: Partial<IInitAppLayout> | undefined;
            if (typeof (urlFeatureTooltip) != 'undefined') {
                ftArgs = {
                    featureTooltipsEnabled: urlFeatureTooltip
                }
            }
            let amArgs: Partial<IInitAppLayout> | undefined;
            if (urlMap) {
                amArgs = {
                    initialActiveMap: urlMap
                };
            }
            let ivArgs: Partial<IInitAppLayout> | undefined;
            if (urlX && urlY && urlScale) {
                ivArgs = {
                    initialView: {
                        x: urlX,
                        y: urlY,
                        scale: urlScale
                    }
                };
            }
            let slArgs: Partial<IInitAppLayout> | undefined;
            if (urlShowLayers) {
                slArgs = {
                    initialShowLayers: [...urlShowLayers]
                };
            }
            let hlArgs: Partial<IInitAppLayout> | undefined;
            if (urlHideLayers) {
                hlArgs = {
                    initialHideLayers: [...urlHideLayers]
                };
            }
            let sgArgs: Partial<IInitAppLayout> | undefined;
            if (urlShowGroups) {
                sgArgs = {
                    initialShowGroups: [...urlShowGroups]
                };
            }
            let hgArgs: Partial<IInitAppLayout> | undefined;
            if (urlHideGroups) {
                hgArgs = {
                    initialHideGroups: [...urlHideGroups]
                };
            }
            const args: IInitAppLayout = {
                ...{
                    resourceId: urlResource ?? resourceId,
                    locale: urlLocale ?? locale ?? DEFAULT_LOCALE,
                    externalBaseLayers: externalBaseLayers,
                    session: urlSession ?? mapguide?.session,
                    onInit: onInit,
                    layout: typeof (this.props.layout) == 'string' ? this.props.layout : undefined,
                    appSettings: appSettings
                },
                ...(ftArgs ?? {}),
                ...(amArgs ?? {}),
                ...(ivArgs ?? {}),
                ...(slArgs ?? {}),
                ...(hlArgs ?? {}),
                ...(sgArgs ?? {}),
                ...(hgArgs ?? {})
            };
            initLayout(initCommand, args);
        }
    }
    componentDidUpdate(prevProps: AppInnerProps) {
        const nextProps = this.props;
        const curUrlState = getStateFromUrl();
        //Preserve locale/resources/session if already present in url
        const nextUrlState: IAppUrlState = {
            locale: curUrlState.locale ?? this.props.locale,
            session: curUrlState.session ?? this.props.mapguide?.session
        };
        if (typeof (this.props.resourceId) == 'string') {
            nextUrlState.resource = curUrlState.resource ?? this.props.resourceId
        }
        if (nextProps.featureTooltipsEnabled != prevProps.featureTooltipsEnabled) {
            nextUrlState.ft = nextProps.featureTooltipsEnabled;
        }
        if (nextProps.map != null && prevProps.map != nextProps.map) {
            this.setState({ isLoading: false });
        }
        if (nextProps.activeMapName) {
            const am = nextProps.activeMapName;
            nextUrlState.map = am;
        }
        if (nextProps.map) {
            if (nextProps.map.currentView) {
                const { x, y, scale } = nextProps.map.currentView;
                nextUrlState.x = x;
                nextUrlState.y = y;
                nextUrlState.scale = scale;
            }
            const mgs = nextProps.map.mapguide;
            if (mgs) {
                const rtm = mgs.runtimeMap;
                const { showGroups, showLayers, hideGroups, hideLayers } = mgs;
                const sg = [] as string[];
                const hg = [] as string[];
                const sl = [] as string[];
                const hl = [] as string[];
                if (rtm?.Group) {
                    for (const g of rtm.Group) {
                        if (showGroups.indexOf(g.ObjectId) >= 0) {
                            sg.push(g.Name);
                        } else if (hideGroups.indexOf(g.ObjectId) >= 0) {
                            hg.push(g.Name);
                        }
                    }
                }
                if (rtm?.Layer) {
                    for (const l of rtm.Layer) {
                        if (showLayers.indexOf(l.ObjectId) >= 0) {
                            sl.push(l.Name);
                        } else if (hideLayers.indexOf(l.ObjectId) >= 0) {
                            hl.push(l.Name);
                        }
                    }
                }
                if (sg.length > 0)
                    nextUrlState.sg = sg;
                if (hg.length > 0)
                    nextUrlState.hg = hg;
                if (sl.length > 0)
                    nextUrlState.sl = sl;
                if (hl.length > 0)
                    nextUrlState.hl = hl;
                if (rtm) {
                    const { SessionId } = rtm;
                    nextUrlState.session = SessionId;
                }
            }
        }
        if (!areStatesEqual(curUrlState, nextUrlState))
            updateUrl(nextUrlState, undefined, nextProps.urlPropsIgnore);
        //else
        //    console.log("Skip pointless url state update");
    }
    private renderErrorMessage(err: Error | InitError, locale: string, args: any): JSX.Element {
        const msg = err.message;
        switch (msg) {
            case "MgConnectionFailedException":
                {
                    const arg = { __html: sanitize(tr("INIT_ERROR_NO_CONNECTION", locale)) };
                    return <div dangerouslySetInnerHTML={arg} />;
                }
            case "MgResourceNotFoundException":
                {
                    const arg = { __html: sanitize(tr("INIT_ERROR_RESOURCE_NOT_FOUND", locale, { resourceId: args.resourceId })) };
                    return <div dangerouslySetInnerHTML={arg} />;
                }
            case "MgSessionExpiredException":
                {
                    const arg = { __html: sanitize(tr("INIT_ERROR_EXPIRED_SESSION", locale, { sessionId: args.session })) };
                    return <div dangerouslySetInnerHTML={arg} />;
                }
            default:
                {
                    const arg = { __html: sanitize(msg) };
                    const stack = normalizeStack(err);
                    return <div>
                        <div dangerouslySetInnerHTML={arg} />
                        {(() => {
                            if (this.props.includeStack === true && stack.length > 0) {
                                return <div>
                                    <p>Stack Trace</p>
                                    <ul>
                                        {stack.map((ln, i) => <li key={`stack-line-${i}`}>{ln}</li>)}
                                    </ul>
                                </div>;
                            }
                        })()}
                    </div>;
                }
        }
    }
    private initErrorRenderer = (err: Error | InitError) => {
        const { configuredLocale, initOptions } = this.props;
        let locale = configuredLocale;
        if (initOptions && initOptions.locale) {
            locale = initOptions.locale;
        }
        //Not showing stack as the error cases are well-defined here and we know where they
        //originate from
        return <Callout intent={Intent.DANGER} title={tr("INIT_ERROR_TITLE", locale)} icon="error">
            {this.renderErrorMessage(err, locale, initOptions || {})}
        </Callout>;
    }
    render(): JSX.Element {
        const { layout, configuredLocale, error } = this.props;
        const { isLoading } = this.state;
        this.props.mapguide

        if (error) {
            return <Error error={error} errorRenderer={this.initErrorRenderer} />
        } else {
            //NOTE: Locale may not have been set at this point, so use default
            const locale = configuredLocale;
            if (isLoading) {
                return <NonIdealState
                    icon={<Spinner intent={Intent.NONE} size={Spinner.SIZE_LARGE} />}
                    title={tr("INIT", locale)}
                    description={tr("INIT_DESC", locale)} />;
            } else {
                const layoutEl = typeof (layout) == 'string' ? getLayout(layout) : layout.factory;
                if (layoutEl) {
                    return <AppContextProvider mapguide={this.props.mapguide}>
                        {layoutEl()}
                    </AppContextProvider>
                } else {
                    return <Error error={tr("ERR_UNREGISTERED_LAYOUT", locale, { layout: layout })} />;
                }
            }
        }
    }
}

export const App = (props: IAppProps) => {
    const error = useInitError();
    const includeStack = useInitErrorStack();
    const initOptions = useInitErrorOptions();
    const configuredLocale = useViewerLocale();
    const map = useActiveMapBranch();
    const activeMapName = useActiveMapName();
    const ftEnabled = useViewerFeatureTooltipsEnabled();

    const dispatch = useReduxDispatch();
    const initLayoutAction = (cmd: IViewerInitCommand, args: IInitAppLayout) => dispatch(initLayout(cmd, args));
    const setElementVisibility = (state: IElementState) => dispatch(setElementStates(state));
    return <AppInner error={error}
        includeStack={includeStack}
        initOptions={initOptions}
        featureTooltipsEnabled={ftEnabled}
        configuredLocale={configuredLocale}
        map={map}
        activeMapName={activeMapName}
        initLayout={initLayoutAction}
        setElementVisibility={setElementVisibility}
        {...props} />;
}